import { useSyncExternalStore } from 'react';

/** The phone install guide the company server serves beside the web app (docs/guides/src/mobile-install.html). */
export const MOBILE_GUIDE = '/help/mobile-install.html';

/** The messenger runs as an installed app (home screen or desktop window), not in a browser tab. */
export function isInstalledApp(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as { standalone?: boolean }).standalone === true;
}

/**
 * iPhone or iPad in a browser tab: Safari offers notifications only to a web app added to the home
 * screen (iOS 16.4 and later), so the way to them is installing it.
 */
export function needsHomeScreenForNotifications(): boolean {
  return mobileOs() === 'ios' && !isInstalledApp();
}

// Which phone, which browser

export type MobileOs = 'android' | 'ios';

interface Agent {
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
}

const currentAgent = (): Agent | null => (typeof navigator === 'undefined' ? null : navigator);

/** Android or iPhone/iPad (an iPad asks for desktop sites and reports a Mac with touch); null elsewhere. */
export function mobileOs(agent: Agent | null = currentAgent()): MobileOs | null {
  if (!agent) return null;
  if (/Android/i.test(agent.userAgent)) return 'android';
  if (/iPhone|iPad|iPod/.test(agent.userAgent) || (agent.platform === 'MacIntel' && (agent.maxTouchPoints ?? 0) > 1)) return 'ios';
  return null;
}

/**
 * Browsers inside other apps. None of them can add a web app to the home screen (it would open in that
 * app again), so the way is opening the messenger in the phone's browser first.
 */
export type InAppBrowser = 'kakaotalk' | 'naver' | 'line' | 'instagram' | 'facebook' | 'band' | 'daum' | 'webview';

const IN_APP: Array<[InAppBrowser, RegExp]> = [
  ['kakaotalk', /KAKAOTALK/i],
  ['naver', /NAVER\(inapp|NAVER\(higgs/i],
  ['line', /\bLine\//],
  ['instagram', /Instagram/],
  ['facebook', /FBAN|FBAV|FB_IAB/],
  ['band', /\bBAND\//],
  ['daum', /DaumApps/],
];

export function inAppBrowser(agent: Agent | null = currentAgent()): InAppBrowser | null {
  if (!agent) return null;
  const found = IN_APP.find(([, pattern]) => pattern.test(agent.userAgent));
  if (found) return found[0];
  // Android's WebView marks itself "; wv)"; a browser app never does.
  return /Android/i.test(agent.userAgent) && /;\s*wv\)/.test(agent.userAgent) ? 'webview' : null;
}

export const IN_APP_NAMES: Record<InAppBrowser, string> = {
  kakaotalk: '카카오톡',
  naver: '네이버 앱',
  line: '라인',
  instagram: '인스타그램',
  facebook: '페이스북',
  band: '밴드',
  daum: '다음 앱',
  webview: '이 앱',
};

export type IosBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other';

function iosBrowser(userAgent: string): IosBrowser {
  if (/CriOS/.test(userAgent)) return 'chrome';
  if (/EdgiOS/.test(userAgent)) return 'edge';
  if (/FxiOS/.test(userAgent)) return 'firefox';
  return /Safari\//.test(userAgent) && !/Whale|NAVER|KAKAOTALK/.test(userAgent) ? 'safari' : 'other';
}

/** iOS major.minor from "OS 17_4 like Mac OS X"; an iPad that reports a Mac has a current iOS. */
function iosVersion(userAgent: string): number | null {
  const match = /OS (\d+)[_.](\d+)/.exec(userAgent);
  return match ? Number(match[1]) + Number(match[2]) / 10 : null;
}

export type AndroidBrowser = 'chrome' | 'samsung' | 'other';

function androidBrowser(userAgent: string): AndroidBrowser {
  if (/SamsungBrowser/.test(userAgent)) return 'samsung';
  // Edge, Opera, Whale and the rest also say "Chrome/": only the plain one is Chrome.
  return /Chrome\//.test(userAgent) && !/EdgA|OPR|Whale|YaBrowser|Firefox/.test(userAgent) ? 'chrome' : 'other';
}

// How to put the messenger on this phone's home screen

export type InstallPlan =
  | { kind: 'installed' }
  | { kind: 'desktop' }
  /** The browser offered its install dialog (Chrome, Samsung Internet, Edge on Android): one tap. */
  | { kind: 'prompt' }
  /** Inside another app: open the messenger in the phone's browser. `url` opens it there; null: by hand. */
  | { kind: 'open-outside'; os: MobileOs; app: InAppBrowser; url: string | null }
  /** Android browser without the dialog: its menu. */
  | { kind: 'android-menu'; browser: AndroidBrowser }
  /** iPhone/iPad: the share sheet's 홈 화면에 추가 (no web page can open it). */
  | { kind: 'ios-share'; browser: IosBrowser }
  /** iOS before 16.4 in another browser than Safari: only Safari adds web apps there. */
  | { kind: 'ios-safari-only' };

/** Opens `url` outside the app the page is in, where that app has a way; null otherwise. */
function outsideUrl(os: MobileOs, app: InAppBrowser, url: string): string | null {
  if (app === 'kakaotalk') return `kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`;
  if (app === 'line') return `${url}${url.includes('?') ? '&' : '?'}openExternalBrowser=1`;
  if (os === 'android') {
    const { host, pathname, search } = new URL(url);
    return `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
  }
  return null;
}

export function installPlan(input: { agent?: Agent | null; installed?: boolean; canPrompt?: boolean; url?: string } = {}): InstallPlan {
  const agent = input.agent === undefined ? currentAgent() : input.agent;
  if (input.installed ?? isInstalledApp()) return { kind: 'installed' };
  const os = mobileOs(agent);
  if (!os || !agent) return { kind: 'desktop' };
  const app = inAppBrowser(agent);
  if (app) {
    const url = input.url ?? (typeof location === 'undefined' ? 'https://localhost/' : `${location.origin}/`);
    return { kind: 'open-outside', os, app, url: outsideUrl(os, app, url) };
  }
  if (os === 'android') return input.canPrompt ? { kind: 'prompt' } : { kind: 'android-menu', browser: androidBrowser(agent.userAgent) };
  const browser = iosBrowser(agent.userAgent);
  const version = iosVersion(agent.userAgent);
  // Other iOS browsers can add web apps from iOS 16.4.
  if (browser !== 'safari' && version !== null && version < 16.4) return { kind: 'ios-safari-only' };
  return { kind: 'ios-share', browser };
}

// The install dialog Android browsers offer (beforeinstallprompt), kept for the moment the person taps

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let offered: BeforeInstallPromptEvent | null = null;
let installedHere = false;
let watching = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

/**
 * Web only, once at start: keeps the browser's install offer instead of its own mini bar, so the messenger
 * can offer it where it explains what installing does. The offer can come before the page is drawn.
 */
export function watchInstallOffer(target: Window = window): void {
  if (watching) return;
  watching = true;
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    offered = event as BeforeInstallPromptEvent;
    changed();
  });
  target.addEventListener('appinstalled', () => {
    offered = null;
    installedHere = true;
    changed();
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => `${offered ? 'offer' : ''}|${installedHere ? 'installed' : ''}`;

export function useInstallOffer(): { canPrompt: boolean; installedHere: boolean; prompt: () => Promise<'accepted' | 'dismissed' | 'unavailable'> } {
  useSyncExternalStore(subscribe, snapshot, snapshot);
  return {
    canPrompt: offered !== null,
    installedHere,
    prompt: async () => {
      const event = offered;
      if (!event) return 'unavailable';
      // A browser shows its dialog once per offer.
      offered = null;
      changed();
      await event.prompt();
      return (await event.userChoice).outcome;
    },
  };
}

/** Tests: forget the offer and the listeners' state. */
export function resetInstallOfferForTests(): void {
  offered = null;
  installedHere = false;
  watching = false;
  changed();
}

// The banner above the conversations

const DISMISSED = 'sd-install-hint-dismissed';

export function installHintDismissed(): boolean {
  try {
    return globalThis.localStorage?.getItem(DISMISSED) === '1';
  } catch {
    return false;
  }
}

export function dismissInstallHint(): void {
  try {
    globalThis.localStorage?.setItem(DISMISSED, '1');
  } catch {
    // Private mode: closed for this page only.
  }
}
